/**
 * Lectura y validación de un catálogo en CSV.
 *
 * Portado de la v1 sin cambiarle el comportamiento: es el mismo parser que ya
 * digirió los archivos reales de los proveedores, y cada regla de acá está
 * puesta por un archivo concreto que se rompía.
 *
 * Dos detalles que rompen estas cargas en Chile y que aquí se contemplan:
 *
 *   · Excel en español exporta con **punto y coma**, no con coma, porque la
 *     coma ya se usa como separador decimal. El delimitador se detecta solo.
 *   · Los números vienen como "1.234,56": punto de miles y coma decimal.
 *
 * El parser vive en el dominio —no en el navegador ni en la API— porque las
 * dos capas lo necesitan: el cliente lo corre para dar respuesta inmediata sin
 * viajes inútiles al servidor, y el servidor REVALIDA lo que le llega con el
 * mismo código. La verdad final sigue estando en la base: el SKU único global
 * y las columnas no nulas vuelven a comprobar todo.
 */
export const COLUMNAS_OBLIGATORIAS = [
    'sku',
    'nombre',
    'marca',
    'mpn',
    'categoria',
    'costo_neto',
    'peso_kg',
    'largo_cm',
    'ancho_cm',
    'alto_cm',
];
export const COLUMNAS_OPCIONALES = ['descripcion', 'marca_fabricante', 'stock'];
const TODAS = [...COLUMNAS_OBLIGATORIAS, ...COLUMNAS_OPCIONALES];
/**
 * Delimitadores candidatos. El ORDEN importa: la detección ordena por cuántos
 * campos produce cada uno y el `sort` de V8 es estable, así que un empate
 * conserva este orden y gana `;`, que es lo que exporta Excel en español.
 */
const DELIMITADORES = [';', ',', '\t'];
/** El byte order mark que Excel de Windows pone al principio del archivo. */
const BOM = '﻿';
/** Quita tildes y espacios para que "Categoría" y "categoria" sean lo mismo. */
function normalizarEncabezado(s) {
    return s
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_');
}
/**
 * Divide una línea respetando comillas dobles, que es como Excel escapa los
 * campos que contienen el delimitador. Sin esto, una descripción con punto y
 * coma parte la fila en dos. Dentro de un campo entrecomillado, `""` es una
 * comilla literal, que es la forma en que Excel la escapa.
 */
function partirLinea(linea, delim) {
    const campos = [];
    let actual = '';
    let entreComillas = false;
    for (let i = 0; i < linea.length; i++) {
        // `charAt` en vez de índice: devuelve siempre una cadena, así que no hay
        // un `undefined` que termine concatenado como texto.
        const c = linea.charAt(i);
        if (entreComillas) {
            if (c === '"') {
                if (linea.charAt(i + 1) === '"') {
                    actual += '"';
                    i++;
                }
                else
                    entreComillas = false;
            }
            else
                actual += c;
        }
        else if (c === '"') {
            entreComillas = true;
        }
        else if (c === delim) {
            campos.push(actual);
            actual = '';
        }
        else
            actual += c;
    }
    campos.push(actual);
    return campos.map((c) => c.trim());
}
/**
 * Convierte "1.234,56" y "1234.56" a número.
 *
 * La ambigüedad real está en "1.234": ¿mil doscientos treinta y cuatro, o uno
 * coma doscientos treinta y cuatro? Se resuelve por la regla de los miles: un
 * punto seguido de exactamente tres dígitos y sin coma en la cadena es
 * separador de miles. Así "1.234" da 1234 y "1.23" da 1.23.
 */
export function aNumero(bruto) {
    const s = bruto.trim();
    if (!s)
        return null;
    let limpio = s.replace(/\s/g, '');
    if (limpio.includes(',')) {
        // Hay coma: es el separador decimal y los puntos son de miles.
        limpio = limpio.replace(/\./g, '').replace(',', '.');
    }
    else if (/^-?\d{1,3}(\.\d{3})+$/.test(limpio)) {
        limpio = limpio.replace(/\./g, '');
    }
    const n = Number(limpio);
    return Number.isFinite(n) ? n : null;
}
function validarFila(datos, linea) {
    const errores = [];
    const salida = {};
    for (const col of COLUMNAS_OBLIGATORIAS) {
        if (!datos[col]?.trim())
            errores.push(`falta ${col}`);
    }
    salida.sku = datos.sku?.trim().toUpperCase();
    salida.nombre = datos.nombre?.trim();
    salida.marca = datos.marca?.trim();
    salida.mpn = datos.mpn?.trim();
    salida.categoria = datos.categoria?.trim();
    salida.descripcion = datos.descripcion?.trim() ?? '';
    // Sin marca de fabricante se asume la comercial: es lo que pasa en la mayoría
    // de los archivos, donde ambas coinciden y la columna viene vacía.
    salida.marca_fabricante = datos.marca_fabricante?.trim() || salida.marca || '';
    const numericos = [
        ['costo_neto', 'costo_neto', true],
        ['peso_kg', 'peso_kg', true],
        ['largo_cm', 'largo_cm', true],
        ['ancho_cm', 'ancho_cm', true],
        ['alto_cm', 'alto_cm', true],
    ];
    for (const [campo, col, mayorQueCero] of numericos) {
        const n = aNumero(datos[col] ?? '');
        if (n === null) {
            if (datos[col]?.trim())
                errores.push(`${col} no es un número: "${datos[col]}"`);
            continue;
        }
        // Regla del negocio: sin peso ni medidas no se puede cotizar el flete.
        if (mayorQueCero && n <= 0)
            errores.push(`${col} debe ser mayor que cero`);
        salida[campo] = n;
    }
    if (datos.stock?.trim()) {
        const n = aNumero(datos.stock);
        if (n === null)
            errores.push(`stock no es un número: "${datos.stock}"`);
        else if (n < 0)
            errores.push('stock no puede ser negativo');
        else
            salida.stock = Math.round(n);
    }
    else {
        // Vacío NO es cero: significa "no tocar el stock de este producto".
        salida.stock = null;
    }
    if (salida.costo_neto !== undefined && !Number.isInteger(salida.costo_neto)) {
        salida.costo_neto = Math.round(salida.costo_neto);
    }
    return { linea, datos: salida, errores };
}
export function leerCsv(texto) {
    // Excel de Windows suele empezar con BOM; si no se quita, la primera
    // columna del encabezado deja de reconocerse.
    const limpio = texto.startsWith(BOM) ? texto.slice(BOM.length) : texto;
    const lineas = limpio.split(/\r?\n/).filter((l) => l.trim() !== '');
    if (lineas.length < 2) {
        return { filas: [], validas: [], columnasDesconocidas: [], delimitador: ';' };
    }
    // El delimitador es el que más campos produce en el encabezado.
    const cabecera = lineas[0] ?? '';
    const candidatos = DELIMITADORES.map((d) => ({ d, n: cabecera.split(d).length })).sort((a, b) => b.n - a.n);
    const delimitador = candidatos[0]?.d ?? ';';
    const encabezados = partirLinea(cabecera, delimitador).map(normalizarEncabezado);
    const columnasDesconocidas = encabezados.filter((h) => h && !TODAS.includes(h));
    const filas = [];
    const vistos = new Map();
    for (let i = 1; i < lineas.length; i++) {
        const campos = partirLinea(lineas[i] ?? '', delimitador);
        const datos = {};
        encabezados.forEach((h, j) => {
            if (h)
                datos[h] = campos[j] ?? '';
        });
        const fila = validarFila(datos, i + 1);
        // Un SKU repetido dentro del mismo archivo haría que la segunda aparición
        // pise a la primera sin aviso.
        if (fila.datos.sku) {
            const antes = vistos.get(fila.datos.sku);
            if (antes)
                fila.errores.push(`SKU repetido (ya aparece en la línea ${antes})`);
            else
                vistos.set(fila.datos.sku, i + 1);
        }
        filas.push(fila);
    }
    const validas = filas.filter((f) => f.errores.length === 0).map((f) => f.datos);
    return { filas, validas, columnasDesconocidas, delimitador };
}
/** Plantilla de ejemplo para que el proveedor no adivine el formato. */
export function plantillaCsv() {
    const encabezado = [...COLUMNAS_OBLIGATORIAS, ...COLUMNAS_OPCIONALES].join(';');
    const ejemplo = [
        'SA-EPP-031',
        'Guantes de nitrilo espuma calibre 15 (par)',
        'Ansell',
        '11-618',
        'epp',
        '1850',
        '0,09',
        '24',
        '12',
        '3',
        'Soporte de nylon con recubrimiento de nitrilo espuma',
        'Ansell',
        '2400',
    ].join(';');
    // BOM al inicio: sin él, Excel abre el archivo sin reconocer los acentos.
    return BOM + encabezado + '\n' + ejemplo + '\n';
}
//# sourceMappingURL=csv.js.map